import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PopupWin from '../PopupWin';
import PopupButton from '../PopupButton';
import * as licenseActions from '../../redux/License/actions';
import './CreateLicenseModal.scss';

const CreateLicenseModal = ({ visible, onClose, onSuccess }) => {
	const dispatch = useDispatch();
	const [selectedTier, setSelectedTier] = useState(null);
	const [error, setError] = useState(null);
	const [createdLicense, setCreatedLicense] = useState(null);
	const [step, setStep] = useState('select'); // 'select' or 'success'
	const [showCopySuccess, setShowCopySuccess] = useState(false);
	
	// Get pricing tiers from Redux state
	const pricingTiers = useSelector(state => state.license.pricingTiers);
	const reduxLoading = useSelector(state => state.license.loading);
	const reduxError = useSelector(state => state.license.error);
	
	// Separate loading state for creating license
	const [isCreating, setIsCreating] = useState(false);

	const handleClose = useCallback(() => {
		onClose();
		setStep('select');
		setCreatedLicense(null);
		setSelectedTier(null);
		setError(null);
		setShowCopySuccess(false);
		setIsCreating(false);
		// Remove focus from any element
		if (document.activeElement) {
			document.activeElement.blur();
		}
	}, [onClose]);

	useEffect(() => {
		if (visible) {
			dispatch(licenseActions.loadPricingTiers());
			setStep('select');
			setCreatedLicense(null);
			setSelectedTier(null);
			setError(null);
			setShowCopySuccess(false);
		}
	}, [visible, dispatch]);

	// Handle ESC key to close modal
	useEffect(() => {
		const handleEscKey = (event) => {
			if (event.key === 'Escape' && visible) {
				handleClose();
			}
		};

		if (visible) {
			document.addEventListener('keydown', handleEscKey);
			// Remove focus from any element when modal opens
			if (document.activeElement) {
				document.activeElement.blur();
			}
		}

		return () => {
			document.removeEventListener('keydown', handleEscKey);
		};
	}, [visible, handleClose]);

	const handleCreateLicense = async () => {
		if (!selectedTier) {
			setError('Please select a pricing tier');
			return;
		}

		try {
			setError(null);
			setIsCreating(true);

			const result = await dispatch(licenseActions.createLicense(selectedTier.id));

			if (result && result.status === 'ok') {
				console.log('License created result:', result); // Debug log
				setCreatedLicense({
					licenseKey: result.data?.license_key || 'N/A',
					status: result.data?.status || 'pending',
					pricingTierName: result.data?.pricing_tier_name || selectedTier.name,
					pricingTierDuration: result.data?.pricing_tier_duration || selectedTier.duration
				});
				setStep('success');
				if (onSuccess) {
					onSuccess();
				}
			} else {
				setError(result?.message || 'Failed to create license');
			}
		} catch (error) {
			setError('Failed to create license');
		} finally {
			setIsCreating(false);
		}
	};

	const renderSelectStep = () => (
		<div className="create-license-content">
			<div className="form-section">
				<h3>Select Pricing Tier</h3>
				<p className="description">
					Choose a pricing tier for the new license. The license will be created with the selected tier configuration.
				</p>

				{reduxLoading && pricingTiers.length === 0 ? (
					<div className="loading-section">
						<div className="loading-spinner"></div>
						<div className="loading-text">Loading pricing tiers...</div>
					</div>
				) : pricingTiers.length === 0 ? (
					<div className="empty-section">
						<div className="empty-text">No pricing tiers available</div>
					</div>
				) : (
					<div className="pricing-tiers">
						{pricingTiers.map((tier) => (
							<div
								key={tier.id}
								className={`pricing-tier-card ${selectedTier?.id === tier.id ? 'selected' : ''}`}
								onClick={() => setSelectedTier(tier)}
								tabIndex={selectedTier?.id === tier.id ? 0 : -1}
								role="button"
								aria-pressed={selectedTier?.id === tier.id}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										setSelectedTier(tier);
									}
								}}
							>
								<div className="tier-header">
									<div className="tier-name">{tier.name}</div>
									<div className="tier-duration">{tier.duration} days</div>
								</div>
								<div className="tier-description">
									License duration: {tier.duration} days
								</div>
							</div>
						))}
					</div>
				)}

				{(error || reduxError) && (
					<div className="error-message">
						{error || reduxError}
					</div>
				)}

				{!selectedTier && (
					<div className="validation-message" style={{ marginTop: '10px' }}>
						Please select a pricing tier to continue
					</div>
				)}
			</div>
		</div>
	);

	const renderSuccessStep = () => (
		<div className="create-license-content">
				<div className="success-section">
					<div className="success-icon">✓</div>
					<h3>License Created Successfully!</h3>
					<p className="success-description">
						The license has been created with the following details:
					</p>

					<div className="license-details">
						<div className="detail-row">
							<span className="detail-label">License Key:</span>
							<div className="detail-value-container">
								<span className="detail-value license-key">
									{createdLicense.licenseKey || 'Generating...'}
								</span>
								{createdLicense.licenseKey && createdLicense.licenseKey !== 'N/A' && (
									<button
										className="copy-button"
										onClick={async () => {
											try {
												await navigator.clipboard.writeText(createdLicense.licenseKey);
												setShowCopySuccess(true);
												setTimeout(() => setShowCopySuccess(false), 2000);
											} catch (error) {
												console.error('Failed to copy to clipboard:', error);
											}
										}}
										title="Copy to clipboard"
									>
										📋
									</button>
								)}
								{showCopySuccess && (
									<span className="copy-success">Copied!</span>
								)}
							</div>
						</div>
						<div className="detail-row">
							<span className="detail-label">Status:</span>
							<span className="detail-value status-pending">
								{createdLicense.status.charAt(0).toUpperCase() + createdLicense.status.slice(1)}
							</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">Pricing Tier:</span>
							<span className="detail-value">{createdLicense.pricingTierName}</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">Duration:</span>
							<span className="detail-value">{createdLicense.pricingTierDuration} days</span>
						</div>
					</div>
				</div>
		</div>
	);

	const selectStepFooter = useMemo(() => (
		<>
			<PopupButton
				variant="secondary"
				onClick={handleClose}
				disabled={isCreating}
			>
				Cancel
			</PopupButton>
			<PopupButton
				variant="primary"
				onClick={handleCreateLicense}
				disabled={!selectedTier}
				loading={isCreating}
			>
				{isCreating ? 'Creating...' : 'Create License'}
			</PopupButton>
		</>
	), [handleClose, handleCreateLicense, selectedTier, isCreating]);

	const successStepFooter = useMemo(() => (
		<PopupButton
			variant="primary"
			onClick={handleClose}
		>
			Close
		</PopupButton>
	), [handleClose]);

	let content;
	try {
		content = (
			<PopupWin
				visible={visible}
				onClose={handleClose}
				title={step === 'select' ? 'Create New License' : 'License Created'}
				footer={step === 'select' ? selectStepFooter : successStepFooter}
			>
				{step === 'select' ? renderSelectStep() : renderSuccessStep()}
			</PopupWin>
		);
	} catch (error) {
		console.error('Error in CreateLicenseModal:', error);
		content = (
			<PopupWin
				visible={visible}
				onClose={handleClose}
				title="Error"
				footer={
					<PopupButton
						variant="primary"
						onClick={handleClose}
					>
						Close
					</PopupButton>
				}
			>
				<div className="create-license-content">
					<div className="error-section">
						<div className="error-icon">⚠️</div>
						<h3>Something went wrong</h3>
						<p>An error occurred while loading the modal. Please try again.</p>
					</div>
				</div>
			</PopupWin>
		);
	}
	return content;
};

CreateLicenseModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func
};

CreateLicenseModal.defaultProps = {
	onSuccess: () => {}
};

export default memo(CreateLicenseModal); 